import { Link } from "react-router-dom";
import { withTeamDividers } from "@/components/playerrow/TeamDivider";
import { Crown, HelpCircle, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ARENA_LABEL, dayLabel, formSlots, formTip, liveCell, paceTone, playerHit } from "./arenaData";
import { FORM_DOTS } from "@/lib/preview";

const ICON = { starters: Crown, bench: Users, unverified: HelpCircle };
const OVR = { out: "OUT", active: "ΠΑΙΖΕΙ", starter: "ΒΑΣ." };
const OVR_LABEL = { out: "OUT — δεν παίζει", active: "Παίζει σίγουρα", starter: "Βασικός" };

function FormBoxes({ row, metric, n, scope }) {
  const slots = formSlots(row, n, scope);
  if (!slots.length) return <span className="la-muted">No games</span>;
  const avg = row.recent?.avg?.[metric];
  const proj = metric === "min" ? row.min : row.stats?.[metric]?.proj;
  const projText = proj == null ? null : String(Math.round(proj));
  // Projection dot: yellow pre-game and while the game is live; on a final result it turns
  // green when the projection was met (confirmed) or red when it was missed.
  const isFinal = row.mode === "final";
  const actual = isFinal ? (metric === "min" ? row.live?.min : row.live?.[metric]) : null;
  const projTone = isFinal && actual != null && proj != null
    ? (actual >= Math.round(proj) ? "proj-hit" : "proj-miss") : "";
  const projTip = projTone
    ? `Projection ${Math.round(proj)} · final ${actual}\n${projTone === "proj-hit" ? "Confirmed ✓" : "Missed ✕"}`
    : `Pre-game projection\n${ARENA_LABEL[metric]}`;
  return (
    <>
      {projText != null && (
        <span className={`rl-box rl-proj ${projTone} ${FORM_DOTS ? "rl-dot" : ""}`} data-tip={projTip} data-testid={`arena-form-proj-${row.player_id}`}>{projText}</span>
      )}
      <span className="rl-dots-wrap">
      {slots.map((g, i) => {
        const v = g.missing ? null : g[metric];
        if (g.missing || v == null) {
          const tip = g.missing ? `${dayLabel(g.date)}\nDid not play / no box score` : `${dayLabel(g.date)}\nNo ${ARENA_LABEL[metric]} recorded`;
          return <span key={`m-${g.date}-${i}`} className={`rl-box rl-missing ${FORM_DOTS ? "rl-dot" : ""}`} data-tip={tip} data-testid={`arena-form-missing-${row.player_id}-${i}`}>–</span>;
        }
        const tone = avg == null || v === avg ? "even" : v > avg ? "up" : "down";
        return <span key={`${g.date}-${i}`} className={`rl-box ${tone} ${FORM_DOTS ? "rl-dot" : ""}`} data-tip={formTip(g)} data-testid={`arena-form-${row.player_id}-${i}`}>{v}</span>;
      })}
      </span>
    </>
  );
}

// Foul trouble ring on the photo: 3 grey, 4 pink, 5 red, 6 = fouled out (red + OUT).
const foulLevel = (pf) => (pf >= 6 ? "out" : pf >= 3 ? String(pf) : null);

// Mini box per player: pre-game projection / live value, matching the pregame table.
// The live number is coloured by pace (ahead green / behind red / on-pace neutral).
// ESPN mode: clean raw box-score number only (no projection, no colour).
function LiveCell({ row, stat, metric, espn }) {
  const c = liveCell(row, stat);
  const L = row.live || {};
  // Concise hover: 3PM -> "made/att - pct%", REB -> "D dreb / O oreb".
  const tip = stat === "fg3m" && Number.isInteger(L.fg3a)
    ? `${L.fg3m ?? 0}/${L.fg3a} - ${L.fg3a > 0 ? Math.round((L.fg3m / L.fg3a) * 100) : 0}%`
    : stat === "reb" && (Number.isInteger(L.dreb) || Number.isInteger(L.oreb))
    ? `D ${L.dreb ?? 0} / O ${L.oreb ?? 0}` : null;
  if (espn) {
    return (
      <span className={`la-cell la-espn ${metric === stat ? "is-metric" : ""} ${c.invalid ? "is-invalid" : ""}`}
        title={c.invalid ? `Invalid non-integer value from provider: ${c.raw}` : (tip || ARENA_LABEL[stat])}
        data-testid={`arena-${stat}-${row.player_id}`}>
        <em className="la-raw" data-testid={`arena-live-${stat}-${row.player_id}`}>{c.text}</em>
      </span>
    );
  }
  const projRaw = stat === "min" ? row.min : row.stats?.[stat]?.proj;
  const proj = projRaw == null ? null : Math.round(projRaw);
  const { tone, expected } = paceTone(row, stat);
  const paceTip = expected != null && stat !== "min" ? ` · on pace ~${Math.round(expected)}` : "";
  return (
    <span className={`la-cell ${metric === stat ? "is-metric" : ""} ${c.invalid ? "is-invalid" : ""}`}
      title={c.invalid ? `Invalid non-integer value from provider: ${c.raw}` : (tip || `Projection / live · ${ARENA_LABEL[stat]}${paceTip}`)}
      data-testid={`arena-${stat}-${row.player_id}`}>
      <b className="la-proj" data-testid={`arena-proj-${stat}-${row.player_id}`}>{proj ?? "—"}</b>
      <i>/</i>
      <em className={tone || ""} data-testid={`arena-live-${stat}-${row.player_id}`}>{c.text}</em>
    </span>
  );
}

function Row({ row, cols, showForm, metric, n, scope, grid }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const pf = Number.isInteger(row.live?.pf) ? row.live.pf : 0;
  const level = foulLevel(pf);
  const espn = scope === "espn";
  const cold = !espn && paceTone(row, metric).cold;
  const hp = playerHit(row, metric);
  const photoTitle = [hp ? `${ARENA_LABEL[metric]} ${hp.pct}%` : null,
    pf ? `${pf} foul${pf > 1 ? "s" : ""}${level === "out" ? " — fouled out" : ""}` : null].filter(Boolean).join(" · ") || undefined;
  return (
    <div className={`la-row ${level === "out" ? "is-fouled-out" : ""} ${cold ? "is-cold" : ""}`} style={{ gridTemplateColumns: grid }} data-testid={`arena-row-${row.player_id}`}>
      <Link to={`/player/${row.player_id}?game=${row.game_id}`} className="la-player" data-testid={`arena-player-${row.player_id}`}>
        <strong className="la-num">{row.jersey ?? "—"}</strong>
        <span className={`la-photo ${row.euro ? "is-euro" : ""} ${level ? `la-foul-${level}` : ""}`} title={photoTitle}
          data-testid={`arena-photo-${row.player_id}`} data-fouls={pf} data-hit={hp ? hp.pct : undefined}>
          {row.photo ? <img src={row.photo} alt="" loading="lazy" /> : null}
          {level === "out" && <em className="la-foul-badge" data-testid={`arena-fouled-out-${row.player_id}`}>OUT</em>}
        </span>
        <span className="la-id"><b>{row.name}</b><small>{row.position || "—"} · {row.team}</small></span>
        {isAdmin && row.override && OVR[row.override] && (
          <em className={`la-ovr ovr-${row.override}`} title={`Χειροκίνητη ρύθμιση admin: ${OVR_LABEL[row.override]}`} data-testid={`arena-override-${row.player_id}`}>⚙ {OVR[row.override]}</em>
        )}
        {cold && <em className="la-cold" title={`Behind pace on ${ARENA_LABEL[metric]}`} data-testid={`arena-cold-${row.player_id}`}>COLD</em>}
      </Link>
      {cols.map((s) => <LiveCell key={s} row={row} stat={s} metric={metric} espn={espn} />)}
      {showForm && <span className="la-form rl-boxes" data-testid={`arena-form-${row.player_id}`}><FormBoxes row={row} metric={metric} n={n} scope={scope} /></span>}
    </div>
  );
}

// Team box-score totals row: sums each visible stat over the team's players (projection / live, like the cells).
export function ArenaTotals({ teamCode, rows, cols, showForm, scope }) {
  const grid = `minmax(230px,2.4fr) ${cols.map(() => "minmax(74px,.8fr)").join(" ")}${showForm ? " minmax(230px,2.4fr)" : ""}`;
  const espn = scope === "espn";
  const liveSum = (s) => rows.reduce((a, r) => a + (Number.isInteger(r.live?.[s]) ? r.live[s] : 0), 0);
  const projSum = (s) => {
    let any = false;
    const t = rows.reduce((a, r) => { const p = s === "min" ? r.min : r.stats?.[s]?.proj; if (p != null) { any = true; return a + p; } return a; }, 0);
    return any ? Math.round(t) : null;
  };
  const shoot = { fg: [liveSum("fgm"), liveSum("fga")], tp: [liveSum("fg3m"), liveSum("fg3a")], ft: [liveSum("ftm"), liveSum("fta")] };
  const hasShooting = shoot.fg[1] > 0 || shoot.tp[1] > 0 || shoot.ft[1] > 0;
  return (
    <section className="la-group la-teamtotals" data-testid={`arena-totals-${teamCode}`}>
      <div className="la-group-label"><span>TEAM</span></div>
      <div className="la-grid">
        <div className="la-row la-totalrow" style={{ gridTemplateColumns: grid }} data-testid={`arena-totalrow-${teamCode}`}>
          <span className="la-total-label">{teamCode} · Σύνολο</span>
          {cols.map((s) => (
            <span key={s} className="la-cell" data-testid={`arena-total-${s}-${teamCode}`}>
              {espn ? <em>{liveSum(s)}</em> : <><b className="la-proj">{projSum(s) ?? "—"}</b><i>/</i><em>{liveSum(s)}</em></>}
            </span>
          ))}
          {showForm && <span className="la-form" />}
        </div>
        {hasShooting && (
          <div className="la-shootbox" data-testid={`arena-shoot-${teamCode}`}>
            {[["FG", shoot.fg], ["3PT", shoot.tp], ["FT", shoot.ft]].map(([label, [m, a]]) => (
              <div className="la-shoot-col" key={label} data-testid={`arena-shoot-${label}-${teamCode}`}>
                <span className="la-shoot-h">{label}</span>
                <span className="la-shoot-ma">{m}-{a}</span>
                <span className="la-shoot-pct">{a > 0 ? Math.round((m / a) * 100) : 0}%</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}


export function ArenaGroup({ kind, title, rows, cols, showForm, onHide, onPickMetric, metric, n, scope }) {
  const Icon = ICON[kind];
  const SRC = { last_game: "last game", nba: "NBA.com", rotowire: "RotoWire", manual: "admin", override: "admin" };
  const srcs = [...new Set(rows.map((r) => r.starter_source).filter((s) => s && s !== "espn"))];
  const projected = srcs.length > 0;
  const grid = `minmax(230px,2.4fr) ${cols.map(() => "minmax(74px,.8fr)").join(" ")}${showForm ? " minmax(230px,2.4fr)" : ""}`;
  return (
    <section className={`la-group la-${kind}`} data-testid={`arena-group-${kind}`}>
      <div className="la-group-label"><Icon size={18} /><span>{title}</span><small>({rows.length})</small>
        {projected && <em className="la-projected" data-testid={`arena-projected-${kind}`}>Projected · {srcs.map((s) => SRC[s] || s).join(" / ")}</em>}
      </div>
      <div className="la-grid">
        <div className="la-head" style={{ gridTemplateColumns: grid }}>
          <span className="la-head-player">Player</span>
          {cols.map((s) => (
            <button key={s} type="button" onClick={() => onPickMetric(s)} aria-pressed={metric === s} className={metric === s ? "is-metric-head" : ""} aria-label={`Show ${ARENA_LABEL[s]} in form column`} title="Click to show this market in the form column" data-testid={`arena-head-${kind}-${s}`}>{ARENA_LABEL[s]}</button>
          ))}
          {showForm && <span className="la-head-form" data-testid={`arena-head-${kind}-form`}>Form · Last {n} · {ARENA_LABEL[metric]}</span>}
        </div>
        {withTeamDividers(rows, (r) => <Row row={r} cols={cols} showForm={showForm} metric={metric} n={n} scope={scope} grid={grid} />)}
        {!rows.length && <p className="la-empty">{kind === "starters" ? "Starters not confirmed" : "No players"}</p>}
      </div>
    </section>
  );
}
